import React from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

type InlineFeedbackVariant = 'error' | 'warning' | 'success' | 'info';

const feedbackStyles: Record<
  InlineFeedbackVariant,
  {
    container: string;
    icon: string;
  }
> = {
  error: {
    container: 'border-red-200 bg-red-50 text-red-800',
    icon: 'text-red-600',
  },
  warning: {
    container: 'border-amber-200 bg-amber-50 text-amber-900',
    icon: 'text-amber-600',
  },
  success: {
    container: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    icon: 'text-emerald-600',
  },
  info: {
    container: 'border-sky-200 bg-sky-50 text-sky-800',
    icon: 'text-sky-600',
  },
};

const feedbackIcons = {
  error: AlertCircle,
  warning: AlertTriangle,
  success: CheckCircle2,
  info: Info,
};

export interface InlineFeedbackProps {
  variant?: InlineFeedbackVariant;
  title?: string;
  message: React.ReactNode;
  className?: string;
}

export const InlineFeedback: React.FC<InlineFeedbackProps> = ({
  variant = 'error',
  title,
  message,
  className,
}) => {
  const styles = feedbackStyles[variant];
  const Icon = feedbackIcons[variant];

  return (
    <div
      role={variant === 'error' || variant === 'warning' ? 'alert' : 'status'}
      className={cn(
        'flex w-full items-start gap-2 rounded-lg border px-3 py-2.5 text-sm leading-5',
        styles.container,
        className,
      )}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', styles.icon)} aria-hidden="true" />
      <div className="min-w-0">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div className={cn(title && 'mt-0.5')}>{message}</div>
      </div>
    </div>
  );
};

export default InlineFeedback;
